import { Component, OnInit, DestroyRef, inject } from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatButtonModule } from "@angular/material/button"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { MatIconModule } from "@angular/material/icon"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { SnackbarService } from "../../services/snackbar.service"
import {
  StationScheduleService,
  PreviewScheduleResponse,
  CreateScheduleResponse,
  PreviewScheduleRequest,
} from "../../services/station-schedule.service"
import { StationService } from "../../services/station.service"
import { PermissionService } from "../../services/permission.service"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { CartService, clearCartCheckoutHandoff } from "../../services/cart.service"
import { PENDING_PROMO_CONTEXT_KEY } from "../my-offers/my-offers.component"
import { parseIsoCalendarDate } from "../../utils/business-date"
import { describeScheduleCreateError } from "../../utils/payment-errors"

@Component({
  selector: "app-campaign-preview",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PageHeaderComponent,
  ],
  templateUrl: "./campaign-preview.component.html",
  styleUrl: "./campaign-preview.component.css",
})
export class CampaignPreviewComponent implements OnInit {
  preview: PreviewScheduleResponse["preview"] | null = null
  campaignRequest: PreviewScheduleRequest | null = null
  previewDetails: any = null
  submitting = false
  addingToCart = false
  loadingAds = false

  /** Latest owner order terms for the selected station (null/empty = none). */
  orderTerms: string | null = null
  loadingOrderTerms = false
  termsAccepted = false
  termsValidationError = false

  userRole: string | null = null
  isAdminOrOwner = false

  /** Fixed page size. The preview API defaults to 10 and page size is not user-selectable. */
  readonly pageSize = 10
  currentPage = 1

  /** Ads for the page currently shown, always one server page. */
  pageAds: any[] = []

  /** Whole-campaign ad count, from the API's pagination metadata. */
  private totalAds = 0

  /**
   * Pages already fetched, keyed by page number.
   *
   * The preview allocation is deterministic for a given campaign request, so a
   * page can be cached safely. It exists to avoid re-running that allocation
   * server-side when the user pages back and forth.
   */
  private pageCache = new Map<number, any[]>()

  private readonly PRIVILEGED_ROLES = ["ADMIN", "OWNER", "STATION_OWNER", "STATION_USER", "STATIONOWNER", "STATIONUSER"]

  constructor(
    private router: Router,
    private snackbar: SnackbarService,
    private scheduleService: StationScheduleService,
    private permissionService: PermissionService,
    private stationService: StationService,
    private cartService: CartService,
  ) { }

  private readonly destroyRef = inject(DestroyRef)

  ngOnInit() {
    // Re-derive on every emission: the effective role follows the selected
    // station and this page is not re-created when the station changes.
    this.permissionService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshPrivilege()
      })

    const navigation = this.router.getCurrentNavigation()
    const state = navigation?.extras?.state || history.state

    if (state?.preview && state?.campaignRequest) {
      this.preview = state.preview
      this.campaignRequest = state.campaignRequest
      this.previewDetails = state.previewDetails
    } else {
      const storedPreview = localStorage.getItem("campaignPreview")
      const storedRequest = localStorage.getItem("campaignRequest")
      const storedDetails = localStorage.getItem("campaignPreviewDetails")

      if (storedPreview && storedRequest) {
        this.preview = JSON.parse(storedPreview)
        this.campaignRequest = JSON.parse(storedRequest)
        if (storedDetails) {
          this.previewDetails = JSON.parse(storedDetails)
        }
      } else {
        this.snackbar.info("No preview data found. Redirecting to campaign builder.")
        this.router.navigate(["/campaignbuilder"])
        return
      }
    }

    // The payload we arrive with IS page 1 (the builder called preview with the
    // default limit of 10), so seed the cache with it instead of re-fetching.
    // Every later page is fetched from the API on demand.
    this.totalAds = this.readTotalAds(this.preview)
    this.pageCache.set(1, [...(this.preview?.scheduled_ads || [])])
    this.currentPage = 1
    this.refreshPageAds()

    this.refreshPrivilege()
    this.applyOwnerOrderTerms()
  }

  /**
   * Staff booking (no payment) follows the caller's role on the station being booked, like
   * create_stationschedule -- not the active station's role or a role held elsewhere.
   */
  private refreshPrivilege(): void {
    const stationId = this.previewDetails?.station_id
    this.userRole = stationId != null
      ? this.permissionService.roleForStation(stationId)
      : this.permissionService.getCurrentRole()
    this.isAdminOrOwner = !!this.userRole
      && this.PRIVILEGED_ROLES.includes(this.userRole.toUpperCase())
  }

  get hasOrderTerms(): boolean {
    return !!this.orderTerms?.trim()
  }

  onTermsAcceptedChange(checked: boolean): void {
    this.termsAccepted = checked
    if (checked) {
      this.termsValidationError = false
    }
  }

  /** Require acknowledgement only when this owner currently has terms configured. */
  private assertTermsAccepted(): boolean {
    if (!this.hasOrderTerms) {
      return true
    }
    if (this.termsAccepted) {
      this.termsValidationError = false
      return true
    }
    this.termsValidationError = true
    this.snackbar.warning("Please confirm that you have read and agree to the owner order terms before continuing.")
    return false
  }

  /**
   * Prefer terms fetched after station selection (passed via previewDetails).
   * Fall back to GET /stations/{id}/owner-order-terms — never call Owner APIs.
   */
  private applyOwnerOrderTerms(): void {
    if (typeof this.previewDetails?.has_order_terms === "boolean") {
      if (this.previewDetails.has_order_terms) {
        const terms =
          typeof this.previewDetails.owner_order_terms === "string"
            ? this.previewDetails.owner_order_terms.trim()
            : ""
        this.orderTerms = terms.length > 0 ? terms : null
      } else {
        this.orderTerms = null
      }
      this.termsAccepted = false
      this.termsValidationError = false
      return
    }

    const stationId = this.previewDetails?.station_id
    if (typeof stationId === "number" && stationId > 0) {
      this.fetchOwnerOrderTerms(stationId)
      return
    }

    this.orderTerms = null
  }

  private fetchOwnerOrderTerms(stationId: number): void {
    this.loadingOrderTerms = true
    this.stationService.getOwnerOrderTerms(stationId).subscribe({
      next: (res) => {
        this.orderTerms = res.has_terms ? res.owner_order_terms : null
        this.termsAccepted = false
        this.termsValidationError = false
        this.loadingOrderTerms = false
      },
      error: (err) => {
        console.error("Failed to load owner order terms:", err)
        this.orderTerms = null
        this.loadingOrderTerms = false
      },
    })
  }

  /**
   * Whole-campaign ad count from a preview payload.
   *
   * Prefers the standard RAD pagination block; falls back to the legacy
   * `scheduled_ads_total` field, then to the page length, so an older cached
   * payload in localStorage still renders.
   */
  private readTotalAds(preview: any): number {
    return (
      preview?.pagination?.total ??
      preview?.scheduled_ads_total ??
      preview?.scheduled_ads?.length ??
      0
    )
  }

  get scheduledAdsTotal(): number {
    return this.totalAds
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.scheduledAdsTotal / this.pageSize))
  }

  get adsStartIndex(): number {
    return (this.currentPage - 1) * this.pageSize + 1
  }

  get paginationStart(): number {
    if (this.scheduledAdsTotal === 0) return 0
    return (this.currentPage - 1) * this.pageSize + 1
  }

  get paginationEnd(): number {
    if (this.pageAds.length === 0) return 0
    return Math.min(this.currentPage * this.pageSize, this.scheduledAdsTotal)
  }

  get showPagination(): boolean {
    return this.scheduledAdsTotal > this.pageSize
  }

  /** Every page comes from the server, so this is just a cache read. */
  private refreshPageAds() {
    this.pageAds = this.pageCache.get(this.currentPage) || []
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage || this.loadingAds) {
      return
    }

    if (this.pageCache.has(page)) {
      this.currentPage = page
      this.refreshPageAds()
      return
    }

    this.fetchAdsPage(page)
  }

  /**
   * Fetch one page of scheduled ads from the preview API.
   *
   * The whole campaign request is re-sent unchanged — station rate, recordings,
   * date range, scheduling mode and target — with only `limit` and `offset` added
   * as query parameters, so every existing preview parameter is preserved across
   * pages and the server re-runs the same deterministic allocation.
   */
  private fetchAdsPage(page: number) {
    if (!this.campaignRequest || !this.preview) return

    this.loadingAds = true
    const offset = (page - 1) * this.pageSize

    this.scheduleService.preview(this.campaignRequest, { limit: this.pageSize, offset }).subscribe({
      next: (res) => {
        const ads = res.preview?.scheduled_ads || []

        // Keep the total in step with the server in case inventory shifted between
        // the builder's first call and this one.
        this.totalAds = this.readTotalAds(res.preview) || this.totalAds

        this.pageCache.set(page, ads)
        this.currentPage = page
        this.refreshPageAds()
        this.loadingAds = false
      },
      error: (err) => {
        console.error("Failed to load scheduled ads page:", err)
        this.snackbar.error(this.snackbar.apiError(err, "Failed to load more scheduled ads."))
        this.loadingAds = false
      },
    })
  }

  getPaginationPages(): number[] {
    const total = this.totalPages
    const current = this.currentPage
    const pages: number[] = []
    const maxButtons = 5

    let start = Math.max(1, current - Math.floor(maxButtons / 2))
    let end = Math.min(total, start + maxButtons - 1)
    start = Math.max(1, end - maxButtons + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    return pages
  }

  trackAd(index: number, ad: any): string | number {
    return ad?.id ?? ad?.scheduledtime ?? `${this.currentPage}-${index}`
  }

  addToCart() {
    if (!this.campaignRequest) {
      this.snackbar.error("Invalid campaign data.")
      return
    }

    if (this.loadingOrderTerms) {
      this.snackbar.info("Please wait while order terms finish loading.")
      return
    }

    if (!this.assertTermsAccepted()) {
      return
    }

    const { payment_intent_id, ...config } = this.campaignRequest as PreviewScheduleRequest & {
      payment_intent_id?: string
    }
    void payment_intent_id

    this.addingToCart = true
    this.cartService.addItem(config).subscribe({
      next: () => {
        this.addingToCart = false
        this.snackbar.success("Added to cart.", 12000, [
          { label: "View Cart", onClick: () => this.router.navigate(["/cart"]) },
          { label: "Add another", onClick: () => this.startAnotherCampaign() },
        ])
      },
      error: (err) => {
        console.error("Add to cart failed:", err)
        this.addingToCart = false
        this.snackbar.error(this.snackbar.apiError(err, "Could not add this campaign to your cart."))
      },
    })
  }

  initiateUserPaymentFlow() {
    if (!this.preview || !this.campaignRequest) {
      this.snackbar.error("Invalid preview data.")
      return
    }

    if (this.loadingOrderTerms) {
      this.snackbar.info("Please wait while order terms finish loading.")
      return
    }

    if (!this.assertTermsAccepted()) {
      return
    }

    localStorage.setItem("campaignPreview", JSON.stringify(this.preview))
    localStorage.setItem("campaignRequest", JSON.stringify(this.campaignRequest))
    clearCartCheckoutHandoff()

    this.router.navigate(["/payment", "new"], {
      state: {
        preview: this.preview,
        campaignRequest: this.campaignRequest,
        previewDetails: this.previewDetails,
      },
    })
  }

  createScheduleDirectly() {
    if (!this.campaignRequest) {
      this.snackbar.error("Invalid campaign data.")
      return
    }

    if (this.loadingOrderTerms) {
      this.snackbar.info("Please wait while order terms finish loading.")
      return
    }

    if (!this.assertTermsAccepted()) {
      return
    }

    const { payment_intent_id, ...rest } = this.campaignRequest as PreviewScheduleRequest & {
      payment_intent_id?: string
    }
    const cleanRequest = rest

    this.submitting = true

    this.scheduleService.create(cleanRequest as any).subscribe({
      next: (response) => {
        this.handleSuccess(response)
      },
      error: (err) => {
        console.error("Campaign creation failed:", err)
        // Staff direct-create is unpaid, so a 422 carries no refund notice here.
        this.snackbar.error(describeScheduleCreateError(err, { paid: false }), 6000)
        this.submitting = false
      },
    })
  }

  handleSuccess(response: CreateScheduleResponse) {
    this.submitting = false
    localStorage.removeItem("campaignPreview")
    localStorage.removeItem("campaignRequest")
    localStorage.removeItem("campaignBuilderState")
    this.snackbar.success("Order confirmed! A confirmation email has been sent to your email address.")
    const orderId = response.order?.id || response.order?.orderid || response.id
    this.router.navigate(["/managecampaigns"], {
      queryParams: { orderid: orderId },
    })
  }

  formatNumber(num: number): string {
    return new Intl.NumberFormat("en-US").format(num)
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  formatDays(daysObj: any): string {
    if (!daysObj) return ""

    const dayMap: Record<string, string> = {
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
      sun: "Sun",
    }

    return Object.keys(daysObj)
      .filter((day) => daysObj[day])
      .map((day) => dayMap[day] || day)
      .join(", ")
  }

  formatDateRange(range: string): string {
    if (!range) return ""
    const parts = range.split(" to ")
    if (parts.length !== 2) return range

    return `${this.formatOnlyDate(parts[0])} to ${this.formatOnlyDate(parts[1])}`
  }

  formatOnlyDate(dateStr: string): string {
    if (!dateStr) return ""
    // new Date("YYYY-MM-DD") is UTC midnight, which renders as the previous day west of UTC.
    const trimmed = dateStr.trim()
    const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? parseIsoCalendarDate(trimmed) : new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  formatReadableDate(dateStr: string): string {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  /** Back to Builder keeps this campaign so it can be edited. */
  goBack() {
    this.router.navigate(["/campaignbuilder"], {
      state: { fromPreview: true },
    })
  }

  /** A new cart campaign starts from an empty builder, not this preview's draft. */
  startAnotherCampaign() {
    localStorage.removeItem("campaignBuilderState")
    localStorage.removeItem("campaignPreview")
    localStorage.removeItem("campaignRequest")
    localStorage.removeItem("campaignPreviewDetails")
    localStorage.removeItem("wasInPreview")
    try {
      localStorage.removeItem(PENDING_PROMO_CONTEXT_KEY)
    } catch {
      /* ignore */
    }
    this.router.navigate(["/campaignbuilder"], {
      state: { freshCampaign: true },
    })
  }
}
